export interface BalanceSheetRow {
  id: string;
  label: string;
  category: string;
  level: 0 | 1 | 2;
  values: Record<string, number>;
  formula?: Record<string, string>;
  format?: 'currency' | 'percentage' | 'number';
  locked?: boolean;
  editable: boolean;
  isTotal?: boolean;
}

interface RawFinancialData {
  node: {
    dbId: string;
    bmType: string;
    category1: string;
    category2?: string;
    category3?: string;
    value: number;
    period: number;
    periodType: string;
    currency: string;
    valuationLevel?: number;
  };
}

// Balance Sheet Category hierarchy
const BALANCE_SHEET_HIERARCHY = {
  assets: {
    label: 'ASSETS',
    level: 0,
    subcategories: {
      current_assets: {
        label: 'Current Assets',
        level: 1,
        items: {
          cash: 'Cash and Cash Equivalents',
          accounts_receivable: 'Accounts Receivable',
          inventory: 'Inventory',
          prepaid_expenses: 'Prepaid Expenses',
          other_current: 'Other Current Assets',
        },
      },
      non_current_assets: {
        label: 'Non-Current Assets',
        level: 1,
        items: {
          property_plant_equipment: 'Property, Plant & Equipment',
          intangible_assets: 'Intangible Assets',
          goodwill: 'Goodwill',
          investments: 'Long-term Investments',
          other_non_current: 'Other Non-Current Assets',
        },
      },
    },
  },
  liabilities: {
    label: 'LIABILITIES',
    level: 0,
    subcategories: {
      current_liabilities: {
        label: 'Current Liabilities',
        level: 1,
        items: {
          accounts_payable: 'Accounts Payable',
          short_term_debt: 'Short-term Debt',
          accrued_liabilities: 'Accrued Liabilities',
          deferred_revenue: 'Deferred Revenue',
          other_current_liabilities: 'Other Current Liabilities',
        },
      },
      non_current_liabilities: {
        label: 'Non-Current Liabilities',
        level: 1,
        items: {
          long_term_debt: 'Long-term Debt',
          deferred_tax_liabilities: 'Deferred Tax Liabilities',
          pension_obligations: 'Pension Obligations',
          other_non_current_liabilities: 'Other Non-Current Liabilities',
        },
      },
    },
  },
  equity: {
    label: "SHAREHOLDERS' EQUITY",
    level: 0,
    subcategories: {
      equity_items: {
        label: 'Equity Components',
        level: 1,
        items: {
          common_stock: 'Common Stock',
          preferred_stock: 'Preferred Stock',
          additional_paid_in_capital: 'Additional Paid-in Capital',
          retained_earnings: 'Retained Earnings',
          treasury_stock: 'Treasury Stock',
          other_comprehensive_income: 'Other Comprehensive Income',
        },
      },
    },
  },
};

export const processBalanceSheetData = (rawData: RawFinancialData[], selectedValuationLevel?: number) => {
  const years = new Set<string>();
  const dataMap = new Map<string, Map<string, { value: number; valuationLevel: number }>>();

  // Extract years and organize data with valuation level logic
  rawData.forEach(item => {
    const { category1, category2, category3, value, period, valuationLevel = 0 } = item.node;

    // Skip if period is null or undefined
    if (!period && period !== 0) {
      return;
    }

    const yearStr = period.toString();
    years.add(yearStr);

    // Filter by selected valuation level if specified
    if (selectedValuationLevel !== undefined && valuationLevel !== selectedValuationLevel) {
      return;
    }

    // Create a key based on categories
    let key = category1;
    if (category2) key += `_${category2}`;
    if (category3) key += `_${category3}`;

    if (!dataMap.has(key)) {
      dataMap.set(key, new Map());
    }

    const existingEntry = dataMap.get(key)?.get(yearStr);

    // If higher valuation level exists, it replaces the lower level
    if (!existingEntry || valuationLevel > existingEntry.valuationLevel) {
      dataMap.get(key)?.set(yearStr, { value, valuationLevel });
    }
  });

  const sortedYears = Array.from(years).sort();
  const rows: BalanceSheetRow[] = [];

  // Build Balance Sheet structure
  Object.entries(BALANCE_SHEET_HIERARCHY).forEach(([mainCategory, mainConfig]) => {
    // Main category header (ASSETS, LIABILITIES, EQUITY)
    const mainRow: BalanceSheetRow = {
      id: mainCategory,
      label: mainConfig.label,
      category: mainCategory,
      level: 0,
      values: {},
      editable: false,
      locked: true,
      format: 'currency',
      isTotal: true,
    };

    let mainCategoryTotal: Record<string, number> = {};
    sortedYears.forEach(year => {
      mainCategoryTotal[year] = 0;
    });

    rows.push(mainRow);

    // Process subcategories
    Object.entries(mainConfig.subcategories).forEach(([subCategoryKey, subConfig]) => {
      // Subcategory header (e.g., Current Assets)
      const subRow: BalanceSheetRow = {
        id: `${mainCategory}_${subCategoryKey}`,
        label: `  ${subConfig.label}`,
        category: mainCategory,
        level: 1,
        values: {},
        editable: false,
        locked: true,
        format: 'currency',
      };

      let subCategoryTotal: Record<string, number> = {};
      sortedYears.forEach(year => {
        subCategoryTotal[year] = 0;
      });

      rows.push(subRow);

      // Process individual items
      if (subConfig.items) {
        Object.entries(subConfig.items).forEach(([itemKey, itemLabel]) => {
          const fullKey = `${mainCategory}_${subCategoryKey}_${itemKey}`;
          const itemData = dataMap.get(fullKey);

          const itemRow: BalanceSheetRow = {
            id: fullKey,
            label: `    ${itemLabel}`,
            category: mainCategory,
            level: 2,
            values: {},
            editable: true,
            format: 'currency',
          };

          sortedYears.forEach(year => {
            const entry = itemData?.get(year);
            const value = entry?.value || 0;
            itemRow.values[year] = value;
            subCategoryTotal[year] += value;
            mainCategoryTotal[year] += value;
          });

          rows.push(itemRow);
        });
      }

      // Add subcategory total
      const subTotalRow: BalanceSheetRow = {
        id: `${mainCategory}_${subCategoryKey}_total`,
        label: `  Total ${subConfig.label}`,
        category: mainCategory,
        level: 1,
        values: subCategoryTotal,
        editable: false,
        locked: true,
        format: 'currency',
        isTotal: true,
      };
      rows.push(subTotalRow);
    });

    // Update main category total
    mainRow.values = mainCategoryTotal;

    // Add total row for main category
    const totalRow: BalanceSheetRow = {
      id: `${mainCategory}_total`,
      label: `Total ${mainConfig.label}`,
      category: mainCategory,
      level: 0,
      values: mainCategoryTotal,
      editable: false,
      locked: true,
      format: 'currency',
      isTotal: true,
    };
    rows.push(totalRow);

    // Add spacer row
    rows.push({
      id: `${mainCategory}_spacer`,
      label: '',
      category: mainCategory,
      level: 0,
      values: {},
      editable: false,
      locked: true,
      format: 'currency',
    });
  });

  // Add final balance check row (Assets = Liabilities + Equity)
  const balanceCheckRow: BalanceSheetRow = {
    id: 'balance_check',
    label: 'BALANCE CHECK (Assets - Liabilities - Equity)',
    category: 'check',
    level: 0,
    values: {},
    editable: false,
    locked: true,
    format: 'currency',
    isTotal: true,
  };

  sortedYears.forEach(year => {
    const assets = rows.find(r => r.id === 'assets_total')?.values[year] || 0;
    const liabilities = rows.find(r => r.id === 'liabilities_total')?.values[year] || 0;
    const equity = rows.find(r => r.id === 'equity_total')?.values[year] || 0;
    balanceCheckRow.values[year] = assets - liabilities - equity;
  });

  rows.push(balanceCheckRow);

  return {
    rows: rows.filter(r => r.label !== ''), // Remove spacer rows from final output
    years: sortedYears,
  };
};

export const transformBalanceSheetToSpreadsheet = (rows: BalanceSheetRow[], years: string[]): any[][] => {
  const spreadsheetData: any[][] = [];

  // Header row
  spreadsheetData.push(['Item', ...years]);

  // Data rows
  rows.forEach(row => {
    const rowData: any[] = [row.label];
    years.forEach(year => {
      const value = row.values[year] || 0;
      rowData.push(value);
    });
    spreadsheetData.push(rowData);
  });

  return spreadsheetData;
};
